import {logInProduction} from '$lib/utils/logInProduction';
import type {Database} from '$lib/classes/interfaces/database';
import {timer} from '$lib/utils/timer';
import {differenceInCalendarDays, format, getDate, getDay, subDays, subYears} from 'date-fns';
import type {PastWeekDatapoint, PastWeekData} from '$lib/classes/_types/past-week';
import {untrack} from 'svelte';
import type {HeatmapData, HeatmapDatapoint, HeatmapMonth, HeatmapWeek} from '$lib/classes/_types/heatmap';
import type {ReviewCountDoc} from '$shared/review-count-doc';

export class ReviewCount {

    private reviewCountDoc: ReviewCountDoc = $state.raw({}) //raw state so that derived pastWeek depends on changes of the whole object

    private _current: number = $state(0)
    get current() { return this._current }

    private get keyOfToday() {
        return format(new Date(), 'yyyy-MM-dd')
    }

    private uid: string | null = null
    private cancelDocSubscription = () => {}

    private interval: ReturnType<typeof setInterval> | undefined = undefined
    private readonly CHECK_KEY_INTERVAL = 60000;

    constructor(
        private readonly database: Database,
        private readonly onNewDay: () => void
    ) {}

    private checkForNewDay() {
        const key = this.keyOfToday
        const savedCount = this.reviewCountDoc[key]
        if (savedCount === undefined) {
            this.onNewDay() //track in Sentry
            logInProduction('NEW DAY >>')
            this.setAndSave(key, 0)
        }
    }

    private setAndSave(key: string, count: number) {
        //optimistic update >> subscription callback will only trigger on remote changes
        //$state.raw need reassignment to trigger reactivity
        this.reviewCountDoc = {
            ...this.reviewCountDoc,
            [key]: count,
        }
        this._current = count //thought about eventually deriving from reviewCountDoc, but implicitly setting is probably clearer

        // TODO proper error handling - how?!?
        if (this.uid) {
            this.database.setReviewCountDoc(this.uid, {[key]: count})
                .catch(error => {
                    console.error('ERROR writing reviewCountDoc', error)
                })
        }
    }

    async init(userId: string): Promise<void> {
        const timer_reviewCount = timer('reviewCount.init')
        this.uid = userId

        try {
            const {unsubscribe, firstValue} = this.database.subscribeReviewCountDoc(userId, (doc) => {
                const key = this.keyOfToday
                if (doc === null) {
                    this.setAndSave(key, 0)
                } else {
                    this.reviewCountDoc = doc

                    const savedCount = doc[key]
                    if (savedCount === undefined) {
                        this.setAndSave(key, 0)
                    } else {
                        this._current = savedCount
                    }
                }
            })
            this.cancelDocSubscription = unsubscribe
            await firstValue // the first value is handled via callback
        } catch (error) {
            throw new Error('ERROR reviewCount.init - subscribeReviewCountDoc', {cause: error})
        }

        this.interval = setInterval(() => this.checkForNewDay(), this.CHECK_KEY_INTERVAL)
        timer_reviewCount(`>> ${this._current}`)
    }

    terminate() {
        this.cancelDocSubscription()
        this.cancelDocSubscription = () => {}

        clearInterval(this.interval)
        this.interval = undefined

        this.uid = null
    }

    addOne() {
        // NOTE addOne will be blocked by RemoteChanges - Why did I note this? Why/when/how is it important?
        const key = this.keyOfToday
        const currentCount = this.reviewCountDoc[key] ?? 0 // might be undefined on a new day
        const newCount = currentCount + 1
        logInProduction(`rc >> ${currentCount}+1 >> ${newCount}`)
        this.setAndSave(key, newCount)
    }

    // STREAK/HEATMAP

    pastWeekData: PastWeekData = $derived.by(() => {

        const rtf = new Intl.RelativeTimeFormat('en-GB', {numeric: 'auto'})
        const todayString = rtf.format(0, 'day') // set locales to 'undefined' >> 'today', 'heute', 'hoy'

        const formatter = new Intl.DateTimeFormat('en-GB', {
            weekday: 'short',
        })
        const today = new Date()
        const daysBack = 6

        let datapoints: PastWeekDatapoint[] = []

        // last 7 days with today at the beginning
        for (let i = 0; i <= daysBack; i++) {
            const day = subDays(today, i)
            const dayDateString = format(day, 'yyyy-MM-dd')
            const count = this.reviewCountDoc[dayDateString] ?? 0
            datapoints.push({
                isToday: i === 0,
                x: i === 0 ? todayString : formatter.format(day).charAt(0),
                y: count,
                streak: (i === 0 || i === 1) ? count > 0 : datapoints[i - 1]?.streak && count > 0,
            })
        }
        const isStreakMissingToday = !datapoints[0].streak && datapoints[1].streak

        return {
            isStreakMissingToday: isStreakMissingToday,
            days: datapoints.reverse(),
        }
    })

    private readonly monthFormatter = new Intl.DateTimeFormat('en-GB', {
        month: 'short',
    })

    private dayOfWeekMondayBased(date: Date) {
        return (getDay(date) + 6) % 7
    }

    get heatmapData(): HeatmapData {

        const reviewCountDoc = untrack(() => this.reviewCountDoc)

        const today = new Date()

        // 365 days back plus days until monday
        const beforeOneYear = subYears(today, 1)
        const beforeOneYearDayOfWeek = this.dayOfWeekMondayBased(beforeOneYear)
        const mondayBeforeOneYear = subDays(beforeOneYear, beforeOneYearDayOfWeek)
        const daysBack = differenceInCalendarDays(today, mondayBeforeOneYear)

        try {
            // all days with today at the beginning
            const datapoints: HeatmapDatapoint[] = []

            for (let i = 0; i <= daysBack; i++) {

                const day = subDays(today, i)
                const dayDateString = format(day, 'yyyy-MM-dd')
                const count = reviewCountDoc[dayDateString] ?? 0

                const streak = (i === 0 || i === 1) ? count > 0
                    : (datapoints[datapoints.length - 1].streak && count > 0)

                datapoints.push({
                    isToday: i === 0,
                    date: day,
                    count: count,
                    streak: streak,
                })
            }

            const todaysDatapoint = datapoints[0]
            const yesterdaysDatapoint = datapoints[1]
            const isStreakMissingToday = !todaysDatapoint.streak && yesterdaysDatapoint.streak

            const weeks = datapoints.reverse().reduce((acc, datapoint) => {
                const dayOfWeek = this.dayOfWeekMondayBased(datapoint.date)
                if (dayOfWeek === 0) {
                    acc.push([datapoint])
                } else {
                    acc[acc.length - 1].push(datapoint)
                }
                return acc
            }, [] as HeatmapWeek[])

            const months = weeks.reduce((acc, week) => {
                const firstDayOfMonth = week.find(datapoint => getDate(datapoint.date) === 1)
                if (firstDayOfMonth) {
                    const month = this.monthFormatter.format(firstDayOfMonth.date)
                    acc.push({
                        month: month,
                        weeks: [week],
                    })
                } else {
                    const lastMonth = acc[acc.length - 1]
                    if (lastMonth) {
                        lastMonth.weeks.push(week)
                    } else {
                        acc.push({
                            month: '',
                            weeks: [week],
                        })
                    }
                }
                return acc
            }, [] as HeatmapMonth[])

            return {
                currentStreak: datapoints.filter(datapoint => datapoint.streak).length,
                isStreakMissingToday: isStreakMissingToday,
                months: months,
            }

        } catch (error) {
            console.error('ERROR generating heatmap data', error)
            logInProduction('today', today, 'beforeOneYear', beforeOneYear, 'mondayBeforeOneYear', mondayBeforeOneYear, 'daysBack', daysBack)
            return {
                currentStreak: 0,
                isStreakMissingToday: false,
                months: [],
            }
        }
    }
}